import React, { useEffect, useMemo, useState } from 'react'
import { useCatalog } from '../../hooks/useCatalog'
import CatalogNav from '../../components/catalog/CatalogNav'

// Phase 3b: taxonomy review queue. The extraction pipeline flags newly-created
// DimensionValues with metadata.needs_review = true (and holds the entries that
// introduced them as drafts). This page lets an admin see those flagged values,
// grouped by dimension, and approve them one at a time.
//
// Admin-only — rendered behind AdminProtectedRoute; the API 403s non-admins.

interface ReviewValue {
  id: number
  name: string
  slug: string
  entry_count: number
  needs_review: boolean
  dimension: { id: number; name: string | null; slug: string | null }
}

export default function TaxonomyReview() {
  const { fetchDimensionValuesNeedingReview, approveDimensionValue } = useCatalog()

  const [values, setValues] = useState<ReviewValue[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [approving, setApproving] = useState<Set<number>>(new Set())
  const [actionError, setActionError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    fetchDimensionValuesNeedingReview()
      .then((data: { values: ReviewValue[] }) => {
        if (!cancelled) setValues(data.values || [])
      })
      .catch((e: any) => !cancelled && setError(e?.message || 'Failed to load review queue'))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [fetchDimensionValuesNeedingReview])

  const approve = async (id: number) => {
    setApproving((prev) => new Set(prev).add(id))
    setActionError(null)
    try {
      await approveDimensionValue(id)
      // Drop it from the queue on success.
      setValues((prev) => prev.filter((v) => v.id !== id))
    } catch (e: any) {
      setActionError(e?.message || 'Could not approve the value')
    } finally {
      setApproving((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    }
  }

  // Group by dimension for section headers, preserving API order.
  const groups = useMemo(() => {
    const byDim = new Map<string, { name: string; values: ReviewValue[] }>()
    for (const v of values) {
      const key = v.dimension?.slug || 'other'
      if (!byDim.has(key)) byDim.set(key, { name: v.dimension?.name || 'Other', values: [] })
      byDim.get(key)!.values.push(v)
    }
    return Array.from(byDim.values())
  }, [values])

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Catalog</h2>
        <p className="mt-1 text-sm text-slate-500">
          Review the taxonomy values the extraction pipeline created automatically. Approving a value
          clears it from this queue; it stays usable as a facet either way.
        </p>
      </div>

      <CatalogNav />

      {actionError && (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{actionError}</div>
      )}

      {loading && <p className="text-sm text-slate-500">Loading review queue…</p>}
      {error && !loading && (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{error}</div>
      )}

      {!loading && !error && values.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
          <p className="text-sm font-medium text-slate-700">Nothing to review.</p>
          <p className="mt-2 text-xs text-slate-500">
            New dimension values created during extraction will show up here for approval.
          </p>
        </div>
      )}

      {!loading && !error && groups.length > 0 && (
        <div className="space-y-6">
          {groups.map((group) => (
            <section key={group.name}>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                {group.name}
                <span className="ml-2 text-slate-400">({group.values.length})</span>
              </h3>
              <ul className="space-y-2">
                {group.values.map((v) => (
                  <li
                    key={v.id}
                    className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50/40 px-4 py-3"
                  >
                    <div className="min-w-0">
                      <span className="text-sm font-medium text-slate-900">{v.name}</span>
                      <span className="ml-2 text-xs text-slate-400">
                        {v.entry_count} {v.entry_count === 1 ? 'entry' : 'entries'}
                      </span>
                    </div>
                    <button
                      type="button"
                      disabled={approving.has(v.id)}
                      onClick={() => approve(v.id)}
                      className="shrink-0 rounded-md bg-accent-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-500 disabled:opacity-50"
                    >
                      {approving.has(v.id) ? 'Approving…' : 'Approve'}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}
